import { commonTypoCorrection } from './common-typos';

/**
 * AutoCorrect decisions. Only obvious typos are fixed: a word on the built-in typo list. Everything else
 * stays an underlined suggestion.
 */
export interface AutocorrectInput {
  /** The finished word exactly as typed. */
  word: string;
  /** The word starts a sentence (start of the paragraph or after . ! ?). */
  sentenceStart: boolean;
  /** Ignored this session or in the person's own dictionary. */
  accepted: boolean;
  /** The person undid an automatic fix of this word earlier in the session. */
  reverted: boolean;
  /** Local dictionary verdict: true correct, false misspelled, undefined not checked yet. */
  correct: boolean | undefined;
}

type Casing = 'lower' | 'capitalised' | 'other';

const LETTERS_ONLY = /^\p{L}+$/u;

export function wordCasing(word: string): Casing {
  if (word === word.toLowerCase()) return 'lower';
  const rest = word.slice(1);
  return word.charAt(0) !== word.charAt(0).toLowerCase() && rest === rest.toLowerCase() ? 'capitalised' : 'other';
}

/** Gives a lower-case correction the typed capitalisation (teh -> the, Teh -> The). */
export function matchCase(typed: string, correction: string): string | null {
  const casing = wordCasing(typed);
  if (casing === 'lower') return correction;
  if (casing === 'capitalised') return correction.charAt(0).toUpperCase() + correction.slice(1);
  return null;
}

/** The automatic correction for a finished word, or null to leave it as a suggestion. */
export function decideAutocorrect(input: AutocorrectInput): string | null {
  const { word } = input;
  if (!word || input.accepted || input.reverted || input.correct === true) return null;
  const casing = wordCasing(word);
  // ALL-CAPS and mixed case (acronyms, product names) are never changed; capitals mid-sentence are likely names.
  if (casing === 'other' || (casing === 'capitalised' && !input.sentenceStart)) return null;
  const listed = LETTERS_ONLY.test(word) ? commonTypoCorrection(word) : undefined;
  return listed ? matchCase(word, listed) : null;
}
